import { FormattedEvent } from '@eventbrite/event-renderer';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { differenceInDays, isWeekend, parse, startOfToday } from 'date-fns';

export enum EVENT_VERTICALS {
    TOP = 'TOP',
    WEEKEND = 'WEEKEND',
    FREE = 'FREE',
    NIGHTLIFE = 'NIGHTLIFE',
    TOURS = 'TOURS',
    ATTRACTIONS = 'ATTRACTIONS',
    CONFERENCES = 'CONFERENCES',
    CONVENTIONS = 'CONVENTIONS',
    EXPOS = 'EXPOS',
    PERFORMANCES = 'PERFORMANCES',
}

//Eventbrite Format ID Mapping
enum EVENT_FORMAT_ID {
    CONFERENCE = 1,
    EXPO = 3,
    CONVENTION = 4,
    PERFORMANCE = 6,
    TOUR = 16,
    ATTRACTION = 17,
}

export const EVENTBRITE_FORMAT = 'EventbriteFormat';
export const CONFERENCE_EVENTBRITE_FORMAT = `${EVENTBRITE_FORMAT}/${EVENT_FORMAT_ID.CONFERENCE}`;
export const EXPO_EVENTBRITE_FORMAT = `${EVENTBRITE_FORMAT}/${EVENT_FORMAT_ID.EXPO}`;
export const CONVENTION_EVENTBRITE_FORMAT = `${EVENTBRITE_FORMAT}/${EVENT_FORMAT_ID.CONVENTION}`;
export const PERFORMANCE_EVENTBRITE_FORMAT = `${EVENTBRITE_FORMAT}/${EVENT_FORMAT_ID.PERFORMANCE}`;
export const TOUR_EVENTBRITE_FORMAT = `${EVENTBRITE_FORMAT}/${EVENT_FORMAT_ID.TOUR}`;
export const ATTRACTION_EVENTBRITE_FORMAT = `${EVENTBRITE_FORMAT}/${EVENT_FORMAT_ID.ATTRACTION}`;

export const CATEGORY_CAROUSEL_TITLE_MAPPING: {
    [key: string]: GenericLazyString;
} = {
    [EVENT_VERTICALS.TOP]: gettext('Top Events'),
    [EVENT_VERTICALS.WEEKEND]: gettext('This Weekend'),
    [EVENT_VERTICALS.FREE]: gettext('Free'),
    [EVENT_VERTICALS.NIGHTLIFE]: gettext('Nightlife'),
    [EVENT_VERTICALS.TOURS]: gettext('Tours'),
    [EVENT_VERTICALS.ATTRACTIONS]: gettext('Attractions'),
    [EVENT_VERTICALS.CONFERENCES]: gettext('Conferences'),
    [EVENT_VERTICALS.CONVENTIONS]: gettext('Conventions'),
    [EVENT_VERTICALS.EXPOS]: gettext('Expos'),
    [EVENT_VERTICALS.PERFORMANCES]: gettext('Performances'),
};

export const isThisWeekendEvent = (event: FormattedEvent) => {
    if (!event.startDate) {
        return false;
    }

    const today = startOfToday();
    const eventDate = parse(event.startDate, 'yyyy-MM-dd', new Date());

    if (isWeekend(eventDate) && differenceInDays(eventDate, today) <= 6) {
        return true;
    }

    return false;
};

export const isFreeEvent = (event: FormattedEvent) => {
    return event.isFree;
};

export const isNightlifeEvent = (event: FormattedEvent) => {
    const eventHour = event.startTime.split(':')[0];

    return eventHour >= '20';
};

export const isTourEvent = (event: FormattedEvent) => {
    if (!event.tags) return false;
    return event.tags.includes(TOUR_EVENTBRITE_FORMAT);
};

export const isConferenceEvent = (event: FormattedEvent) => {
    if (!event.tags) return false;
    return event.tags.includes(CONFERENCE_EVENTBRITE_FORMAT);
};

export const isExpoEvent = (event: FormattedEvent) => {
    if (!event.tags) return false;
    return event.tags.includes(EXPO_EVENTBRITE_FORMAT);
};

export const isConventionEvent = (event: FormattedEvent) => {
    if (!event.tags) return false;
    return event.tags.includes(CONVENTION_EVENTBRITE_FORMAT);
};

export const isPerformanceEvent = (event: FormattedEvent) => {
    if (!event.tags) return false;
    return event.tags.includes(PERFORMANCE_EVENTBRITE_FORMAT);
};

export const isAttractionEvent = (event: FormattedEvent) => {
    if (!event.tags) return false;
    return event.tags.includes(ATTRACTION_EVENTBRITE_FORMAT);
};

// This is a dummy function call to appease the logic in the transformResponse method,
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const isTopEvent = (event: FormattedEvent) => {
    return false;
};

export const TTD_VERTICALS = [
    {
        vertical: EVENT_VERTICALS.WEEKEND,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.WEEKEND],
        eventChecker: isThisWeekendEvent,
    },
    {
        vertical: EVENT_VERTICALS.FREE,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.FREE],
        eventChecker: isFreeEvent,
    },
    {
        vertical: EVENT_VERTICALS.NIGHTLIFE,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.NIGHTLIFE],
        eventChecker: isNightlifeEvent,
    },
    {
        vertical: EVENT_VERTICALS.TOURS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.TOURS],
        eventChecker: isTourEvent,
    },
    {
        vertical: EVENT_VERTICALS.ATTRACTIONS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.ATTRACTIONS],
        eventChecker: isAttractionEvent,
    },
    {
        vertical: EVENT_VERTICALS.CONFERENCES,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.CONFERENCES],
        eventChecker: isConferenceEvent,
    },
    {
        vertical: EVENT_VERTICALS.CONVENTIONS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.CONVENTIONS],
        eventChecker: isConventionEvent,
    },
    {
        vertical: EVENT_VERTICALS.EXPOS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.EXPOS],
        eventChecker: isExpoEvent,
    },
    {
        vertical: EVENT_VERTICALS.PERFORMANCES,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.PERFORMANCES],
        eventChecker: isPerformanceEvent,
    },
];

export const POI_VERTICALS = [
    {
        vertical: EVENT_VERTICALS.TOP,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.TOP],
        eventChecker: isTopEvent,
    },
    {
        vertical: EVENT_VERTICALS.WEEKEND,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.WEEKEND],
        eventChecker: isThisWeekendEvent,
    },
    {
        vertical: EVENT_VERTICALS.FREE,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.FREE],
        eventChecker: isFreeEvent,
    },
    {
        vertical: EVENT_VERTICALS.NIGHTLIFE,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.NIGHTLIFE],
        eventChecker: isNightlifeEvent,
    },
    {
        vertical: EVENT_VERTICALS.TOURS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.TOURS],
        eventChecker: isTourEvent,
    },
    {
        vertical: EVENT_VERTICALS.ATTRACTIONS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.ATTRACTIONS],
        eventChecker: isAttractionEvent,
    },
    {
        vertical: EVENT_VERTICALS.CONFERENCES,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.CONFERENCES],
        eventChecker: isConferenceEvent,
    },
    {
        vertical: EVENT_VERTICALS.CONVENTIONS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.CONVENTIONS],
        eventChecker: isConventionEvent,
    },
    {
        vertical: EVENT_VERTICALS.EXPOS,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.EXPOS],
        eventChecker: isExpoEvent,
    },
    {
        vertical: EVENT_VERTICALS.PERFORMANCES,
        title: CATEGORY_CAROUSEL_TITLE_MAPPING[EVENT_VERTICALS.PERFORMANCES],
        eventChecker: isPerformanceEvent,
    },
];
